import styled from "@emotion/styled";
import { Flex, Spinner } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useStores } from "@stores";

const tvScriptLoadingPromise = () =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.id = "tradingview-widget-loading-script";
    script.src = "https://s3.tradingview.com/tv.js";
    script.type = "text/javascript";
    script.onload = resolve;

    document.head.appendChild(script);
  });

const TRADING_VIEW_ID = "tradingview_chart_container";

const CHART_CHECK_INTERVAL = 1000;

const getNormalName = (market: string): string => {
  if (market === "TKNA") return "OKX:BTCUSDT";

  return market;
};

export const TradingViewBase: React.FC = observer(() => {
  const widgetRef = useRef<any>(null);
  const { marketStore } = useStores();
  const [isLoading, setIsLoading] = useState(true);

  const market = getNormalName(marketStore.baseToken?.symbol ?? "");

  const createWidget = () => {
    const tradingViewContainer = document.getElementById(TRADING_VIEW_ID);
    const isTradingViewAvailable = "TradingView" in window;

    if (!market || !tradingViewContainer || !isTradingViewAvailable) {
      return;
    }

    setIsLoading(true);

    const widgetConfig = {
      autosize: true,
      symbol: market,
      interval: "30",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      enable_publishing: false,
      hide_top_toolbar: true,
      hide_legend: false,
      save_image: false,
      container_id: TRADING_VIEW_ID,
    };
    widgetRef.current = new (window as any).TradingView.widget(widgetConfig);

    if (widgetRef.current) {
      widgetRef.current.onChartReady(() => {
        setIsLoading(false);
      });
    }
  };

  useLayoutEffect(() => {
    tvScriptLoadingPromise().then(createWidget);
  }, [market]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!widgetRef.current?._ready) return;
      clearInterval(intervalId);
    }, CHART_CHECK_INTERVAL);

    return () => clearInterval(intervalId);
  }, [market]);

  return (
    <Wrapper>
      {isLoading && (
        <LoaderOverlay align="center" justify="center">
          <Spinner size="3" />
        </LoaderOverlay>
      )}
      <TradingViewContainer id={TRADING_VIEW_ID} />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  position: relative;
  min-height: 0px;
  height: 100%;
  width: 100%;
`;

const LoaderOverlay = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #141414;
  z-index: 10;
  border-radius: 8px;
`;

const TradingViewContainer = styled.div`
  min-height: 0px;
  height: 100%;
  width: 100%;

  iframe {
    border-radius: 8px;
  }
`;
